import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import './index.css';
import { initializeAuth, supabase } from './lib/supabase';
import { ErrorBoundary, Bugsnag } from './monitoring/bugsnag';

//console.log('🚀 main.tsx: Starting application initialization');

// Initialize the application
async function initializeApp() {
  try {
    if (import.meta.env.DEV) {
      //      console.log('🔐 main.tsx: Initializing authentication...');
    }

    // Initialize authentication first
    await initializeAuth();

    // After auth, set Bugsnag user and metadata
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || 'anonymous';
      Bugsnag.setUser(userId);

      const selectedCountry = localStorage.getItem('selectedCountry') || 'US';
      Bugsnag.addMetadata('context', {
        country: selectedCountry,
        path: window.location.pathname,
        language: navigator.language,
      });
      Bugsnag.addMetadata('build', {
        appVersion: import.meta.env.VITE_APP_VERSION || undefined,
        mode: import.meta.env.MODE,
        commit: import.meta.env.VITE_GIT_COMMIT || undefined,
      });
    } catch (e) {
      // Non-fatal: continue rendering
    }

    if (import.meta.env.DEV) {
      //      console.log('✅ main.tsx: Authentication initialized successfully');
    }

    // Small delay to show the loading message
    await new Promise(resolve => setTimeout(resolve, 500));

    // Render the React app
    if (import.meta.env.DEV) {
      //      console.log('⚛️ main.tsx: Rendering React application');
    }
    const root = ReactDOM.createRoot(document.getElementById('root')!);
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );

    if (import.meta.env.DEV) {
      //      console.log('✅ main.tsx: Application initialized successfully');
    }

  } catch (error) {
    console.error('🚨 main.tsx: Failed to initialize application:', error);

    // Still try to render the app in case it can work without auth
    setTimeout(() => {
      try {
        const root = ReactDOM.createRoot(document.getElementById('root')!);
        root.render(
          <React.StrictMode>
            <ErrorBoundary>
              <App />
            </ErrorBoundary>
          </React.StrictMode>
        );
      } catch (renderError) {
        console.error('🚨 main.tsx: Failed to render app:', renderError);
      }
    }, 2000);
  }
}

// Expose a dev-only helper to trigger a test error
if (import.meta.env.MODE !== 'production') {
  // Call in console: __bugsnagTest()
  (window as any).__bugsnagTest = () =>
    Bugsnag.notify(new Error('Test error'));
}

// Handle unhandled promise rejections and errors globally
window.addEventListener('unhandledrejection', (e) => {
  const r: any = (e as any).reason;
  const msg = String(r?.message || r || '');
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Importing a module script failed')) {
    try { Bugsnag.notify(r instanceof Error ? r : new Error(msg)); } catch { }
    setTimeout(() => window.location.reload(), 100);
  }
});
window.addEventListener('error', (e) => {
  const msg = String((e as ErrorEvent).message || '');
  if (msg.includes('Failed to fetch dynamically imported module') || msg.includes('Importing a module script failed')) {
    try { Bugsnag.notify((e as any).error || new Error(msg)); } catch { }
    setTimeout(() => window.location.reload(), 100);
  }
});

// Start the application
initializeApp();